export function generateNormalRandom(mean: number, stdDev: number): number {
  let u = 0,
    v = 0
  while (u === 0) u = Math.random() // 0を回避
  while (v === 0) v = Math.random() // 0を回避

  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)
  return mean + z * stdDev
}

export interface YearlyStat {
  year: number
  percentiles: {
    p5: number
    p25: number
    p50: number
    p75: number
    p95: number
  }
  mean: number
}

/**
 * 単一の投資シミュレーションを実行する
 * @param monthlyInvestment 月額投資額（数値または月ごとの配列）
 * @param initialInvestment 初期投資額
 * @param years 投資期間（年）
 * @param annualReturnRate 期待年間収益率（%）
 * @param volatility ボラティリティ（%）
 * @returns 年ごとの資産額の配列（インデックス0が初期値、インデックス1が1年後...）
 */
export function runSingleSimulation(
  monthlyInvestment: number | number[],
  initialInvestment: number,
  years: number,
  annualReturnRate: number,
  volatility: number,
): number[] {
  const months = years * 12
  const monthlyReturnMean = annualReturnRate / 12 / 100
  const monthlyVolatility = volatility / Math.sqrt(12) / 100

  let totalAmount = initialInvestment
  const yearlyResults: number[] = [initialInvestment]

  // 投資スケジュールの展開
  const getMonthlyAmount = (monthIndex: number): number => {
    if (typeof monthlyInvestment === 'number') {
      return monthlyInvestment
    }
    // 配列の場合は、その月の投資額を取得（配列長が足りない場合は最後の値を使用）
    // 配列が年単位の場合は、monthIndex / 12 でインデックスを計算するロジックが必要だが、
    // ここではmonthlyInvestmentは「月額」の配列（months長）か、あるいは単一の数値「月額」と仮定するか？
    // LifePlanからは「年間の貯蓄額」が来るので、それを「月額」に直した配列（years長）を渡す仕様にしよう。
    // つまり number[] の場合は "Yearly Monthly Amount" (各年の月額) とする。
    const yearIndex = Math.floor(monthIndex / 12)
    return monthlyInvestment[Math.min(yearIndex, monthlyInvestment.length - 1)]
  }

  // 初期投資額に最初の月のリターンを適用（既存ロジック踏襲）
  const initialReturn = generateNormalRandom(monthlyReturnMean, monthlyVolatility)
  totalAmount *= 1 + initialReturn

  for (let month = 0; month < months; month++) {
    // 毎月の投資額を追加
    totalAmount += getMonthlyAmount(month)

    // その月のランダムなリターンを生成
    const monthlyReturn = generateNormalRandom(monthlyReturnMean, monthlyVolatility)

    // 資産に適用
    totalAmount *= 1 + monthlyReturn

    // 年末の値を記録
    if ((month + 1) % 12 === 0) {
      yearlyResults.push(totalAmount)
    }
  }

  return yearlyResults
}

/**
 * モンテカルロシミュレーションを実行する
 * @param monthlyInvestment 月額投資額（数値または年ごとの月額配列）
 * @param initialInvestment 初期投資額
 * @param years 投資期間（年）
 * @param annualReturnRate 期待年間収益率（%）
 * @param volatility ボラティリティ（%）
 * @param simulationRuns シミュレーション実行回数
 * @param targetAmount 目標金額（オプション）
 * @returns シミュレーション結果
 */
export function runMonteCarloSimulation(
  monthlyInvestment: number | number[],
  initialInvestment: number,
  years: number,
  annualReturnRate: number,
  volatility: number,
  simulationRuns: number,
  targetAmount?: number,
): SimulationResult {
  const finalResults: number[] = []
  // 年ごとの全シミュレーション結果を保持する配列
  // yearlyData[yearIndex][runIndex]
  const yearlyData: number[][] = Array.from({ length: years + 1 }, () => [])

  let successCount = 0

  for (let i = 0; i < simulationRuns; i++) {
    const yearlySequence = runSingleSimulation(
      monthlyInvestment,
      initialInvestment,
      years,
      annualReturnRate,
      volatility
    )

    // 各年の結果を保存
    yearlySequence.forEach((amount, yearIndex) => {
      yearlyData[yearIndex].push(amount)
    })

    const finalAmount = yearlySequence[yearlySequence.length - 1]
    finalResults.push(finalAmount)

    if (targetAmount && finalAmount >= targetAmount) {
      successCount++
    }
  }

  // 最終結果を昇順にソート
  finalResults.sort((a, b) => a - b)

  // 統計情報を計算
  const mean = finalResults.reduce((sum, value) => sum + value, 0) / finalResults.length
  const median = finalResults[Math.floor(finalResults.length / 2)]
  const min = finalResults[0]
  const max = finalResults[finalResults.length - 1]

  // 年ごとの統計情報を計算
  const yearlyStats: YearlyStat[] = yearlyData.map((data, year) => {
    data.sort((a, b) => a - b)
    const yearMean = data.reduce((sum, val) => sum + val, 0) / data.length
    return {
      year,
      mean: yearMean,
      percentiles: {
        p5: data[Math.floor(data.length * 0.05)],
        p25: data[Math.floor(data.length * 0.25)],
        p50: data[Math.floor(data.length * 0.50)], // Median
        p75: data[Math.floor(data.length * 0.75)],
        p95: data[Math.floor(data.length * 0.95)],
      }
    }
  })

  // 総投資額を計算
  let totalInvestment = initialInvestment
  if (typeof monthlyInvestment === 'number') {
    totalInvestment += monthlyInvestment * years * 12
  } else {
    for (let y = 0; y < years; y++) {
      // 各年の月額 * 12
      const monthlyAmount = monthlyInvestment[Math.min(y, monthlyInvestment.length - 1)]
      totalInvestment += monthlyAmount * 12
    }
  }

  // カーネル密度推定から最頻値を計算
  const { x, y } = calculateKernelDensity(finalResults, 200)
  let maxDensityIndex = 0
  let maxDensity = y[0]
  for (let i = 1; i < y.length; i++) {
    if (y[i] > maxDensity) {
      maxDensity = y[i]
      maxDensityIndex = i
    }
  }
  const mode = x[maxDensityIndex]

  // 目標達成確率を計算
  const successRate = targetAmount ? (successCount / simulationRuns) * 100 : 0

  return {
    results: finalResults,
    yearlyStats,
    statistics: {
      mean,
      median,
      min,
      max,
      mode,
      totalInvestment,
    },
    successRate,
    params: {
      investmentAmount: typeof monthlyInvestment === 'number' ? monthlyInvestment : 0, // 互換性のため数値のみ保持（配列の場合は0などにするが、UI側で別途対応が必要かも）
      initialInvestment: initialInvestment,
      investmentPeriod: years,
      returnRate: annualReturnRate,
      volatility: volatility,
      targetAmount: targetAmount || 0,
      investmentSchedule: Array.isArray(monthlyInvestment) ? monthlyInvestment : undefined
    },
    kernelDensity: { x, y },
  }
}

/**
 * シミュレーション結果の型定義
 */
export interface SimulationResult {
  results: number[]
  yearlyStats: YearlyStat[]
  statistics: {
    mean: number
    median: number
    min: number
    max: number
    mode: number
    totalInvestment: number
  }
  successRate: number
  params: {
    investmentAmount: number
    initialInvestment: number
    investmentPeriod: number
    returnRate: number
    volatility: number
    targetAmount: number
    investmentSchedule?: number[]
  }
  kernelDensity: {
    x: number[]
    y: number[]
  }
}

/**
 * ヒストグラムのビン（区間）を計算する
 * @param data データ配列
 * @param binCount ビンの数
 * @returns ビンの配列と各ビンの境界値
 */
export function calculateHistogramBins(
  data: number[],
  binCount = 20,
): {
  bins: number[]
  binBoundaries: number[]
} {
  // 最小値と最大値を計算
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < data.length; i++) {
    const value = data[i];
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  const binSize = (max - min) / binCount;

  // ビンと境界値を事前に割り当て
  const bins = new Array(binCount).fill(0);
  const binBoundaries = new Array(binCount + 1);

  // 境界値を設定
  for (let i = 0; i <= binCount; i++) {
    binBoundaries[i] = min + i * binSize;
  }

  // データをビンに分類
  for (let i = 0; i < data.length; i++) {
    const value = data[i];
    const binIndex = Math.min(Math.floor((value - min) / binSize), binCount - 1);
    bins[binIndex]++;
  }

  return { bins, binBoundaries };
}

/**
 * カーネル密度推定を計算する
 * @param data データ配列
 * @param points 密度を計算する点の数
 * @param bandwidth バンド幅（オプション）
 * @returns 密度推定値の配列と対応するx軸の値
 */
export function calculateKernelDensity(
  data: number[],
  points = 200,
  bandwidth?: number
): { x: number[]; y: number[] } {
  // データの最小値と最大値を計算
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let sumSquared = 0;

  // 1回のループで必要な統計量を計算
  for (let i = 0; i < data.length; i++) {
    const value = data[i];
    min = Math.min(min, value);
    max = Math.max(max, value);
    sum += value;
    sumSquared += value * value;
  }
  const range = max - min;
  const padding = range * 0.2;
  const mean = sum / data.length;
  const variance = (sumSquared / data.length) - (mean * mean);
  const stdDev = Math.sqrt(variance);

  // バンド幅の計算（Silvermanのルール）
  const h = bandwidth || 0.9 * Math.min(stdDev, range / 1.34) * Math.pow(data.length, -0.2);

  // x軸の値を生成
  const x = new Array(points);
  for (let i = 0; i < points; i++) {
    x[i] = Math.max(0, min - padding + (range + 2 * padding) * (i / (points - 1)));
  }
  // 密度を計算
  const y = new Array(points);
  for (let i = 0; i < points; i++) {
    let density = 0;
    const x0 = x[i];
    for (let j = 0; j < data.length; j++) {
      const z = (x0 - data[j]) / h;
      density += Math.exp(-0.5 * z * z);
    }
    y[i] = density / (data.length * h * Math.sqrt(2 * Math.PI));
  }

  // 密度が無視できるほど小さいデータポイントを削除
  const maxDensity = Math.max(...y);
  const threshold = maxDensity * 0.001; // 最大密度の0.1%を閾値とする
  const filteredX: number[] = [];
  const filteredY: number[] = [];

  for (let i = 0; i < points; i++) {
    if (y[i] >= threshold) {
      filteredX.push(x[i]);
      filteredY.push(y[i]);
    }
  }

  // 積分が1になるように正規化
  const dx = (filteredX[filteredX.length - 1] - filteredX[0]) / (filteredX.length - 1);
  const integral = filteredY.reduce((sum, value) => sum + value * dx, 0);
  for (let i = 0; i < filteredY.length; i++) {
    filteredY[i] /= integral;
  }

  return { x: filteredX, y: filteredY };
}

/**
 * 複数の投資資産を考慮したシミュレーションを実行する
 * @param investments 投資資産の情報
 * @param years 投資期間（年）
 * @param simulationRuns シミュレーション実行回数
 * @returns シミュレーション結果
 */
export function runMultiAssetSimulation(
  investments: {
    [key: string]: {
      amount: number;
      return: number;
      volatility: number;
    };
  },
  years: number,
  simulationRuns: number
): MultiAssetSimulationResult {
  // 各資産の運用利回りを計算
  const assetResults: { [key: string]: number } = Object.entries(investments).reduce(
    (acc, [key, asset]) => ({
      ...acc,
      [key]: asset.amount * (1 + asset.return / 100) ** years,
    }),
    {} as { [key: string]: number }
  );

  // 合計資産を計算
  const totalAssets = Object.values(assetResults).reduce(
    (sum: number, value: number) => sum + value,
    0
  );

  const totalInitialInvestment = Object.values(investments).reduce(
    (sum: number, asset) => sum + asset.amount,
    0
  );

  // 各資産の割合を計算
  const assetAllocation: { [key: string]: number } = Object.entries(assetResults).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value / totalAssets,
    }),
    {} as { [key: string]: number }
  );

  const result: AssetSimulationResult = {
    totalAssets,
    assetAllocation,
    assetValues: assetResults,
  };

  return {
    results: [result],
    statistics: {
      mean: totalAssets,
      median: totalAssets,
      min: totalAssets,
      max: totalAssets,
      totalInitialInvestment,
      averageAllocation: assetAllocation,
    },
    params: {
      investmentPeriod: years,
      returns: Object.entries(investments).reduce(
        (acc, [key, asset]) => ({
          ...acc,
          [key]: asset.return,
        }),
        {} as { [key: string]: number }
      ),
      volatilities: Object.entries(investments).reduce(
        (acc, [key, asset]) => ({
          ...acc,
          [key]: asset.volatility,
        }),
        {} as { [key: string]: number }
      ),
    },
  };
}

/**
 * 複数資産シミュレーション結果の型定義
 */
export interface MultiAssetSimulationResult {
  results: AssetSimulationResult[];
  statistics: {
    mean: number;
    median: number;
    min: number;
    max: number;
    totalInitialInvestment: number;
    averageAllocation: {
      [key: string]: number;
    };
  };
  params: {
    investmentPeriod: number;
    returns: {
      [key: string]: number;
    };
    volatilities: {
      [key: string]: number;
    };
  };
}

/**
 * 単一シミュレーションの資産結果の型定義
 */
export interface AssetSimulationResult {
  totalAssets: number;
  assetAllocation: {
    [key: string]: number;
  };
  assetValues: {
    [key: string]: number;
  };
}

